import { useState, useEffect, FC } from "react";
import Button from "react-bootstrap/Button";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { get, size, omit } from "lodash";
import { useCreateDashboardStore, ColorPicker } from ".";
import DashboardCreateSelectDeviceModal from "./modal/SelectDevices";

import { COLORS } from "constant";
import { DEVICE_CHART_CONFIG, DeviceType } from "constant/deviceChart";
import { MultipleChartColumnProps } from "types/Charts";
import { getSensorTypes } from "utils/gatewayFunctions";
import { SensorTypeObject } from "types";
import { displaySensorType } from "utils/functions";
import { assignUniqueColorsToDevices } from "utils/chart";
import CloseOutlined from "@mui/icons-material/CloseOutlined";

const maxDevicesAllowed = 8;

interface MultipleDeviceChartColumnProps extends MultipleChartColumnProps {
    deviceType: DeviceType;
    excludeDevices?: string[];
}

export const MultipleDeviceChartColumn: FC<MultipleDeviceChartColumnProps> = (
    props
) => {
    const { column, onChange, data, isEdit = false, deviceType, excludeDevices = [] } = props;

    const config = DEVICE_CHART_CONFIG[deviceType];

    const [showSelectModal, setShowSelectModal] = useState(false);
    const [selectedDevices, setSelectedDevices] = useState<any>({});

    const [maxDevices, setMaxDevices] = useState<number>(maxDevicesAllowed);
    const [deviceTypeSelector, setDeviceTypeSelector] = useState<
        SensorTypeObject[]
    >([]);

    const [selectedDeviceType, setSelectedDeviceType] = useState<string>(
        get(data, `data[${column}].sensor_type`, "") ||
        get(deviceTypeSelector, `[${column}].id`, "")
    );
    const [eventTrigger, setEventTrigger] = useState<any>();
    const [otherDeviceType, setOtherDeviceType] = useState<string>("");
    const [canDelete, setCanDelete] = useState(true);
    const { deviceList } = useCreateDashboardStore();

    useEffect(() => {
        !deviceTypeSelector.length &&
            setDeviceTypeSelector(
                getSensorTypes(deviceList.filter(config.filterFn))
            );

        if (isEdit) {
            const total: number = Object.keys(get(data, "data", {})).reduce(
                (current: any, key) => {
                    current += size(get(data, `data.${key}.devices`, {}));
                    return current;
                },
                0
            );

            setCanDelete(total > 1);
        }

        Object.keys(get(data, "data", {}))
            .filter((key) => Number(key) !== column)
            .forEach((key) => {
                setMaxDevices(
                    maxDevicesAllowed -
                    size(get(data, `data.${key}.devices`, {}))
                );

                setDeviceTypeSelector(
                    getSensorTypes(deviceList.filter(config.filterFn))
                );
            });

        setSelectedDevices(data.data[column]?.devices || {});

        setSelectedDeviceType(
            get(data, `data[${column}].sensor_type`, "") ||
            get(deviceTypeSelector, `[${column}].id`, "")
        );

        if (data.multipleChartType === "different") {
            const otherColumn = Number(column) === 0 ? 1 : 0;

            setOtherDeviceType(
                get(data, `data[${otherColumn}].sensor_type`, "") ||
                get(deviceTypeSelector, `[${otherColumn}].id`, "")
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, deviceList, deviceTypeSelector.toString()]);

    useEffect(() => {
        eventTrigger &&
            onChange({
                devices: selectedDevices,
                sensor_type: selectedDeviceType,
            });
        setEventTrigger(undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedDevices]);

    // Notify parent of initial type selection when deviceTypeSelector loads
    useEffect(() => {
        if (
            deviceTypeSelector.length > 0 &&
            !data.data[column]?.sensor_type &&
            !isEdit
        ) {
            let initialType = deviceTypeSelector[0]?.id;

            // For "Two different types" mode, column 1 should default to a different type than column 0
            if (data.multipleChartType === "different" && column === 1) {
                const column0Type = data.data[0]?.sensor_type;
                if (column0Type) {
                    // Find the first type that's different from column 0
                    const differentType = deviceTypeSelector.find(
                        (type) => type.id !== column0Type
                    );
                    if (differentType) {
                        initialType = differentType.id;
                    }
                }
            }

            if (initialType) {
                onChange({
                    devices: {},
                    sensor_type: initialType,
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deviceTypeSelector.length]);

    const onRemove = (event: any, key: string) => {
        setSelectedDevices(omit(selectedDevices, key));
        setEventTrigger(event);
    };

    return (
        <Col lg={12} xl={6}>
            <div className="multiple-chart-block__panel rounded p-3">
                <p>
                    {`Select ${config.displayNameLower} type {column}`.fill({
                        column:
                            data.multipleChartType === "same"
                                ? ""
                                : String(column + 1),
                    })}
                </p>

                <Form.Control
                    as="select"
                    custom
                    value={selectedDeviceType}
                    onChange={(event) => {
                        setSelectedDevices({});
                        setSelectedDeviceType(event.target.value);
                        setEventTrigger(event);
                    }}
                    aria-label={`Select ${config.displayNameLower} type`}
                >
                    {deviceTypeSelector.map(({ id, name }) => (
                        <option
                            key={id}
                            value={id}
                            disabled={[
                                otherDeviceType,
                                selectedDeviceType,
                            ].includes(id)}
                        >
                            {name}
                        </option>
                    ))}
                </Form.Control>

                <div className="mt-4 d-flex gap-2 flex-wrap flex-column flex-lg-row">
                    <Button
                        variant="primary"
                        onClick={() => setShowSelectModal(true)}
                    >
                        Select {config.displayNamePlural}
                    </Button>
                    <div className="text-primary-blue-4 align-self-center font-size-13px">
                        Select {config.displayNameLower}s of the chosen{" "}
                        {config.displayNameLower} type.
                    </div>
                </div>

                <DashboardCreateSelectDeviceModal
                    show={showSelectModal}
                    onHide={() => setShowSelectModal(false)}
                    values={selectedDevices}
                    onSave={(event, devices) => {
                        assignUniqueColorsToDevices(
                            devices,
                            Object.values(data.data),
                            COLORS
                        );
                        setSelectedDevices(devices);
                        setEventTrigger(event);
                    }}
                    deviceType={deviceType}
                    sensorTypes={[selectedDeviceType]}
                    maxDevices={maxDevices}
                    excludeDevices={excludeDevices}
                />

                <div className="cstm-table mt-4">
                    {size(selectedDevices) !== 0 && (
                        <div className="table-head" key="head">
                            <Row className="no-checkbox">
                                <Col md={4}>{config.displayName}</Col>
                                <Col md={3}>Type</Col>
                                <Col md={3}>Color</Col>
                                <Col md={2}></Col>
                            </Row>
                        </div>
                    )}

                    {Object.keys(selectedDevices).map((key, index: number) => {
                        const device = selectedDevices[key];
                        return (
                            <div key={key} className="table-row">
                                <Row className="flex-column flex-md-row">
                                    <Col
                                        md={4}
                                        className="no-checkbox d-flex align-items-center flex-row"
                                    >
                                        <Col className="d-block d-md-none multiple-chart-label-wrapper">
                                            {config.displayName}:
                                        </Col>
                                        <Col
                                            className={`text-right text-md-left text-break ${device.sensorName
                                                ? ""
                                                : "text-secondary-red-1"
                                                }`}
                                        >
                                            {!device.sensorName && (
                                                <span className="material-icons mr-2">
                                                    error_outline
                                                </span>
                                            )}
                                            {device.sensorName ??
                                                "Information not found"}
                                        </Col>
                                    </Col>
                                    <Col
                                        md={3}
                                        className="no-checkbox d-flex align-items-center flex-row"
                                    >
                                        <Col className="d-block d-md-none multiple-chart-label-wrapper">
                                            Type:
                                        </Col>
                                        <Col className="text-right text-md-left text-break">
                                            {displaySensorType(device) ?? "-"}
                                        </Col>
                                    </Col>
                                    <Col
                                        md={3}
                                        className="no-checkbox d-flex align-items-center flex-row"
                                    >
                                        <Col className="d-block d-md-none multiple-chart-label-wrapper">
                                            Color:
                                        </Col>
                                        <Col className="text-right text-md-left">
                                            <div className="d-inline-block">
                                                <ColorPicker
                                                    className="bg-blue-gray"
                                                    defaultValue={
                                                        selectedDevices[key]
                                                            .color ||
                                                        COLORS[index]
                                                    }
                                                    onChange={(
                                                        event,
                                                        color
                                                    ) => {
                                                        setEventTrigger(event);
                                                        setSelectedDevices(
                                                            (prev: any) => ({
                                                                ...prev,
                                                                [key]: {
                                                                    ...prev[
                                                                    key
                                                                    ],
                                                                    color,
                                                                },
                                                            })
                                                        );
                                                    }}
                                                />
                                            </div>
                                        </Col>
                                    </Col>
                                    <Col
                                        md={2}
                                        className="d-flex justify-content-end align-items-center order-first order-md-last"
                                    >
                                        {canDelete && (
                                            <CloseOutlined
                                                className="cursor-pointer text-secondary-red-1 mr-2"
                                                onClick={(event) =>
                                                    onRemove(event, key)
                                                }
                                                onKeyDown={(event) =>
                                                    onRemove(event, key)
                                                }
                                            />
                                        )}
                                    </Col>
                                </Row>
                            </div>
                        );
                    })}
                </div>
            </div>
        </Col>
    );
};
